import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  Typography,
  Chip,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  Divider,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import {
  Edit as EditIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
} from "@material-ui/icons";

import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import { head, isArray, has } from "lodash";
import { useDate } from "../../hooks/useDate";

import moment from "moment";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(2),
  },
  fullWidth: {
    width: "100%",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  textfield: {
    width: "100%",
  },
  row: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  control: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },

  // Cabeçalho da tabela
  tableHead: {
    "& th": {
      fontWeight: 700,
      fontSize: "0.75rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.default,
      borderBottom: `2px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    transition: "background-color 0.15s ease",
  },

  // Chips de status — "& .MuiChip-icon" garante que o ícone herde
  // a cor do texto do chip em qualquer tema (evita o ícone apagado no dark mode)
  chipAtivo: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 22,
    "& .MuiChip-icon": { color: "inherit" },
  },
  chipInativo: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 22,
    "& .MuiChip-icon": { color: "inherit" },
  },
  chipCampanha: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 22,
    "& .MuiChip-icon": { color: "inherit" },
  },
  chipVencendo: {
    backgroundColor: "#fff8e1",
    color: "#f57f17",
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 22,
    "& .MuiChip-icon": { color: "inherit" },
  },
  chipVencido: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 22,
    "& .MuiChip-icon": { color: "inherit" },
  },
  chipNormal: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    fontSize: "0.72rem",
    height: 22,
    "& .MuiChip-icon": { color: "inherit" },
  },

  // Cards mobile
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1),
  },
  cardName: {
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  cardRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  cardLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  cardValue: {
    fontSize: "0.82rem",
  },
  cardChips: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },

  // Seção do formulário
  formSectionTitle: {
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  listHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  listTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  companyCount: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}));

// --- Helpers de status visual ---

const dueDateChip = (classes, dueDate) => {
  if (!moment(dueDate).isValid()) return null;
  const diff = moment(dueDate).diff(moment(), "days");
  if (diff <= -1) {
    return <Chip size="small" label="Vencido" className={classes.chipVencido} icon={<CancelIcon style={{ fontSize: 12 }} />} />;
  }
  if (diff <= 5) {
    return <Chip size="small" label="Vencendo" className={classes.chipVencendo} icon={<WarningIcon style={{ fontSize: 12 }} />} />;
  }
  return null;
};

// --- CompanyForm ---

export function CompanyForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecord((prev) => {
      if (moment(initialValue).isValid()) {
        initialValue.dueDate = moment(initialValue.dueDate).format("YYYY-MM-DD");
      }
      return { ...prev, ...initialValue };
    });
  }, [initialValue]);

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    onSubmit(data);
    setRecord({ ...initialValue, dueDate: "" });
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: { companyId: initialValue.id },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      const increments = {
        MENSAL: [1, "month"],
        BIMESTRAL: [2, "month"],
        TRIMESTRAL: [3, "month"],
        SEMESTRAL: [6, "month"],
        ANUAL: [12, "month"],
      };
      const inc = increments[data.recurrence];
      if (inc) {
        data.dueDate = moment(data.dueDate).add(...inc).format("YYYY-MM-DD");
      }
    }
    setRecord(data);
  };

  const isEditing = record.id !== undefined;

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        className={classes.fullWidth}
        initialValues={record}
        onSubmit={(values, { resetForm }) =>
          setTimeout(() => {
            handleSubmit(values);
            resetForm();
          }, 500)
        }
      >
        {() => (
          <Form className={classes.fullWidth}>

            {/* Dados da empresa */}
            <Typography className={classes.formSectionTitle}>
              {isEditing ? "Editar empresa" : "Nova empresa"}
            </Typography>
            <Grid spacing={2} container>
              <Grid xs={12} sm={6} md={4} item>
                <Field as={TextField} label="Nome" name="name" variant="outlined" className={classes.fullWidth} margin="dense" />
              </Grid>
              <Grid xs={12} sm={6} md={3} item>
                <Field as={TextField} label="E-mail" name="email" variant="outlined" className={classes.fullWidth} margin="dense" required />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <Field as={TextField} label="Telefone" name="phone" variant="outlined" className={classes.fullWidth} margin="dense" />
              </Grid>
              <Grid xs={12} sm={6} md={3} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="plan-selection">Plano</InputLabel>
                  <Field as={Select} id="plan-selection" label="Plano" labelId="plan-selection-label" name="planId" margin="dense" required>
                    {plans.map((plan, key) => (
                      <MenuItem key={key} value={plan.id}>{plan.name}</MenuItem>
                    ))}
                  </Field>
                </FormControl>
              </Grid>

              {/* Configurações */}
              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Field as={Select} label="Status" name="status" margin="dense">
                    <MenuItem value={true}>Ativo</MenuItem>
                    <MenuItem value={false}>Inativo</MenuItem>
                  </Field>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel>Campanhas</InputLabel>
                  <Field as={Select} label="Campanhas" name="campaignsEnabled" margin="dense">
                    <MenuItem value={true}>Habilitadas</MenuItem>
                    <MenuItem value={false}>Desabilitadas</MenuItem>
                  </Field>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  label="Vencimento"
                  type="date"
                  name="dueDate"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel>Recorrência</InputLabel>
                  <Field as={Select} label="Recorrência" name="recurrence" margin="dense">
                    <MenuItem value="MENSAL">Mensal</MenuItem>
                  </Field>
                </FormControl>
              </Grid>

              {/* Ações */}
              <Grid xs={12} item>
                <Box display="flex" justifyContent="flex-end" flexWrap="wrap" style={{ gap: 8, paddingTop: 8 }}>
                  <ButtonWithSpinner loading={loading} onClick={() => onCancel()} variant="outlined" size="small">
                    Limpar
                  </ButtonWithSpinner>
                  {isEditing && (
                    <>
                      <ButtonWithSpinner loading={loading} onClick={() => onDelete(record)} variant="contained" color="secondary" size="small">
                        Excluir
                      </ButtonWithSpinner>
                      <ButtonWithSpinner loading={loading} onClick={() => incrementDueDate()} variant="outlined" color="primary" size="small">
                        + Vencimento
                      </ButtonWithSpinner>
                      <ButtonWithSpinner loading={loading} onClick={() => handleOpenModalUsers()} variant="outlined" color="primary" size="small">
                        Ver usuário
                      </ButtonWithSpinner>
                    </>
                  )}
                  <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary" size="small">
                    {isEditing ? "Salvar alterações" : "Cadastrar"}
                  </ButtonWithSpinner>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
}

// --- Grid (tabela desktop) ---

export function CompaniesManagerGrid(props) {
  const { records, onSelect } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { dateToClient } = useDate();

  const renderCampaignsStatus = (row) => {
    if (has(row, "settings") && isArray(row.settings) && row.settings.length > 0) {
      const setting = row.settings.find((s) => s.key === "campaignsEnabled");
      if (setting) return setting.value === "true";
    }
    return false;
  };

  // --- Visualização mobile: cards ---
  if (isMobile) {
    return (
      <Box>
        {records.map((row, key) => (
          <Paper key={key} className={classes.card} elevation={0}>
            <div className={classes.cardHeader}>
              <div>
                <Typography className={classes.cardName}>{row.name || "-"}</Typography>
                <Typography variant="caption" color="textSecondary">{row.email || "-"}</Typography>
              </div>
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => onSelect(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>

            <Divider style={{ marginBottom: 8 }} />

            <div className={classes.cardRow}>
              <Typography className={classes.cardLabel}>Telefone</Typography>
              <Typography className={classes.cardValue}>{row.phone || "-"}</Typography>
            </div>
            <div className={classes.cardRow}>
              <Typography className={classes.cardLabel}>Plano</Typography>
              <Typography className={classes.cardValue}>{row.planId !== null ? row.plan?.name : "-"}</Typography>
            </div>
            <div className={classes.cardRow}>
              <Typography className={classes.cardLabel}>Criada em</Typography>
              <Typography className={classes.cardValue}>{dateToClient(row.createdAt)}</Typography>
            </div>
            <div className={classes.cardRow}>
              <Typography className={classes.cardLabel}>Vencimento</Typography>
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <Typography className={classes.cardValue}>{dateToClient(row.dueDate)}</Typography>
                {dueDateChip(classes, row.dueDate)}
              </Box>
            </div>

            <div className={classes.cardChips}>
              <Chip
                size="small"
                label={row.status === false ? "Inativo" : "Ativo"}
                className={row.status === false ? classes.chipInativo : classes.chipAtivo}
              />
              {renderCampaignsStatus(row) && (
                <Chip size="small" label="Campanhas" className={classes.chipCampanha} />
              )}
            </div>
          </Paper>
        ))}
      </Box>
    );
  }

  // --- Visualização desktop: tabela ---
  return (
    <Paper className={classes.tableContainer} elevation={0} variant="outlined">
      <Table size="small">
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell style={{ width: 40 }} />
            <TableCell>Nome</TableCell>
            <TableCell>E-mail</TableCell>
            <TableCell>Telefone</TableCell>
            <TableCell>Plano</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Campanhas</TableCell>
            <TableCell>Criada em</TableCell>
            <TableCell>Vencimento</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow key={key} className={classes.tableRow}>
              <TableCell>
                <Tooltip title="Editar empresa">
                  <IconButton size="small" onClick={() => onSelect(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {row.name || "-"}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="textSecondary">{row.email || "-"}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="textSecondary">{row.phone || "-"}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{row.planId !== null ? row.plan?.name : "-"}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.status === false ? "Inativo" : "Ativo"}
                  className={row.status === false ? classes.chipInativo : classes.chipAtivo}
                  icon={row.status === false
                    ? <CancelIcon style={{ fontSize: 12 }} />
                    : <CheckCircleIcon style={{ fontSize: 12 }} />
                  }
                />
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={renderCampaignsStatus(row) ? "Habilitadas" : "Desabilitadas"}
                  className={renderCampaignsStatus(row) ? classes.chipCampanha : classes.chipNormal}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="textSecondary">{dateToClient(row.createdAt)}</Typography>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <Typography variant="body2">{dateToClient(row.dueDate)}</Typography>
                  {dueDateChip(classes, row.dueDate)}
                </Box>
                {row.recurrence && (
                  <Typography variant="caption" color="textSecondary">{row.recurrence}</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

// --- CompaniesManager principal ---

export default function CompaniesManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
  });

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      setRecords(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadCompanies();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Verifique se já existe uma empresa com o mesmo nome ou se os campos foram preenchidos corretamente.");
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadCompanies();
      handleCancel();
      toast.success("Empresa excluída com sucesso!");
    } catch (e) {
      toast.error("Não foi possível excluir a empresa.");
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setRecord({
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      campaignsEnabled: false,
      dueDate: "",
      recurrence: "",
    });
  };

  const handleSelect = (data) => {
    let campaignsEnabled = false;
    const setting = data.settings.find((s) => s.key.indexOf("campaignsEnabled") > -1);
    if (setting) {
      campaignsEnabled = setting.value === "true" || setting.value === "enabled";
    }

    setRecord({
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      campaignsEnabled,
      dueDate: data.dueDate || "",
      recurrence: data.recurrence || "",
    });
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <CompanyForm
            initialValue={record}
            onDelete={() => setShowConfirmDialog(true)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>

        <Grid xs={12} item>
          <Divider style={{ marginBottom: 8 }} />
          <Box className={classes.listHeader}>
            <Typography className={classes.listTitle}>Empresas cadastradas</Typography>
            <Typography className={classes.companyCount}>
              {records.length} {records.length === 1 ? "empresa" : "empresas"}
            </Typography>
          </Box>
          <CompaniesManagerGrid records={records} onSelect={handleSelect} />
        </Grid>
      </Grid>

      <ConfirmationModal
        title="Excluir empresa"
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esta empresa? Esta ação não pode ser desfeita.
      </ConfirmationModal>
    </Paper>
  );
}
